import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { AiOutlineMail } from "react-icons/ai";
import { BiGroup, BiPencil } from "react-icons/bi";
import { IoApps } from "react-icons/io5";
import {
    RiEdit2Line, RiInformationFill, RiMailLine, RiPlayCircleFill,
} from "@remixicon/react";
import classNames from "classnames";
import { SiZendesk } from "react-icons/si";
import { Loading } from "../../../components";
import Avatar from "../../../components/avatar/Avatar";
import {
    useGetAccountQuery,
    AgentAssignmentPreference,
    useSaveUserSettingsMutation,
    GetAccountDocument,
    useSaveUserFirstLastNameMutation,
    AgentSoundPreference,
} from "../../../__generated__/api";
import Select from "../../../components/select/Select";
import { useTheme } from "../../../contexts/Theme";
import Styles from "./AccountDetails.module.scss";
import SettingsContainer from "../../../components/theme/settings";
import TopPanel from "../../../components/top-panel/TopPanel";
import useArticleContext, { useHelpContext } from "../../../contexts/HelpContext";
import { ARTICLES } from "../../../components/help/Help";
import Button from "../../../components/theme/button/Button";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import Alert from "../../../components/theme/alert/Alert";
import { ReactComponent as SlackLogo } from "../../../assets/img/slack-logo.svg";
import { ReactComponent as MicrosoftTeams } from "../../../assets/img/microsoft-teams.svg";
import useCurrentSession from "../../../contexts/session";
import useNotifications from "../../../contexts/NotificationContext";

const ASSIGNMENT_PREFERENCES = [
    {
        label: "Automatically synchronize",
        value: AgentAssignmentPreference.AutoSynchronize,
    },
    {
        label: "Always available",
        value: AgentAssignmentPreference.Available,
    },
    {
        label: "Always unavailable",
        value: AgentAssignmentPreference.Unavailable,
    },

];

const NOTIFICATION_PREFERENCES = [
    {
        label: "None",
        value: AgentSoundPreference.None,
    },
    {
        label: "Beyond",
        value: AgentSoundPreference.Beyond,
    },
    {
        label: "Bubble",
        value: AgentSoundPreference.Bubble,
    },
    {
        label: "Level Up",
        value: AgentSoundPreference.LevelUp,
    },
    {
        label: "Live Chat",
        value: AgentSoundPreference.LiveChat,
    },
    {
        label: "Message",
        value: AgentSoundPreference.Message,
    },
    {
        label: "Message 2",
        value: AgentSoundPreference.Message_2,
    },
    {
        label: "Message 3",
        value: AgentSoundPreference.Message_3,
    },
    {
        label: "Notification",
        value: AgentSoundPreference.Notification,
    },
    {
        label: "Pop",
        value: AgentSoundPreference.Pop,
    },

];

const APP_TO_ICON = {
    Slack: <SlackLogo />,
    "Microsoft Teams": <MicrosoftTeams />,
    Zendesk: <SiZendesk />,
};

export default function AccountDetails(): ReactElement {
    useArticleContext(ARTICLES.ACCOUNT_DETAILS);
    const { data, loading: isLoading } = useGetAccountQuery();
    const { saveSounds, sounds } = useCurrentSession();
    const [saveSettings] = useSaveUserSettingsMutation();
    const [saveUser] = useSaveUserFirstLastNameMutation();
    const notifications = useNotifications();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const theme = useTheme();

    const user = data?.session?.user;

    const [assignmentPreference, setAssignmentPreference] = useState(data?.userSettings.assignmentPreference
            ?? AgentAssignmentPreference.AutoSynchronize);

    useEffect(() => {
        if (data?.userSettings?.assignmentPreference) {
            setAssignmentPreference(data.userSettings?.assignmentPreference);
        }
    }, [data]);

    useEffect(() => {
        if (!data || assignmentPreference === data?.userSettings.assignmentPreference) {
            return;
        }
        saveSettings({
            variables: {
                settings: {
                    assignmentPreference,
                },
            },
        });
    }, [assignmentPreference]);

    useEffect(() => {
        if (data?.session?.user) {
            setFirstName(data.session.user.firstName ?? "");
            setLastName(data.session.user.lastName ?? "");
        }
    }, [data?.session?.user.firstName, data?.session?.user.lastName]);

    if (isLoading) {
        return <Loading />;
    }
    if (!user) {
        return <h3 className="text-center text-light mt-3">Not Logged in</h3>;
    }

    const handleChangeUser = async (userId?: string): Promise<void> => {
        await saveUser({
            refetchQueries: [
                { query: GetAccountDocument },
            ],
            variables: {
                id: userId,
                user: {
                    firstName,
                    lastName,
                },
            },
        });
    };

    const applicationHandles = Object.entries(
        data?.session?.user?.handles?.filter(handle => handle.application)
            .groupBy(app => app.application?.applicationName ?? "")
        ?? {},
    );

    return (
        <>
            {theme.isV3 && (
                <TopPanel title="Profile Settings" />
            )}
            <div className="d-flex h-100vh">
                <div className={`col-xl-3 col-lg-4 col-md-6 ${theme.isV3 ? Styles.sidebar : "p-4"}`}>
                    <div className="text-center">
                        <Avatar src={user?.avatarUrl as string} variant="lg-v3" isCircle={theme.isV3} />
                    </div>
                    <div className="mx-2">
                        <h3 className={classNames(
                            theme.isV3 ? Styles["name-container"] : "mt-3 d-flex align-items-center",
                            isEditing ? Styles["name-change-editing"] : "",
                        )}
                        >
                            {user.name}
                            {!isEditing && (
                                theme.isV3 ? (
                                    <Button
                                        variant="none"
                                        prefixIcon={<RiEdit2Line />}
                                        onClick={() => setIsEditing(true)}
                                    />
                                ) : (
                                    <button
                                        aria-label="Save name change"
                                        type="button"
                                        className="btn btn-none p-2"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <BiPencil className="text-light" />
                                    </button>
                                )

                            )}
                        </h3>
                        {isEditing && (

                            <div className="d-flex pt-2 pb-3">
                                <form
                                    className="flex-grow-1"
                                    onSubmit={event => {
                                        event.preventDefault();
                                        setIsEditing(false);
                                        handleChangeUser(user.id);
                                    }}
                                >
                                    {theme.isV3 ? (
                                        <div className={Styles["name-change"]}>
                                            <FormGroup
                                                label="First Name"
                                                type="text"
                                                onChange={element => setFirstName(element.target.value)}
                                                value={firstName}
                                                size="medium"
                                            />
                                            <FormGroup
                                                label="Last Name"
                                                type="text"
                                                onChange={element => setLastName(element.target.value)}
                                                value={lastName}
                                                size="medium"
                                            />
                                            <Alert prefixIcon={<RiInformationFill />} variant="yield">
                                                Once you change your name, it may take 5-10 minutes to
                                                propagate to other Foqal systems. If you still see the old
                                                name after a few minutes, please contact support.
                                            </Alert>
                                            <div className={Styles["name-change-controls"]}>
                                                <Button
                                                    variant="outline-gray"
                                                    size="medium"
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setFirstName(data?.session?.user?.firstName ?? "");
                                                        setLastName(data?.session?.user?.lastName ?? "");
                                                    }}
                                                >
                                                    Cancel
                                                </Button>

                                                <Button
                                                    type="submit"
                                                    size="medium"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <Form.Group className="mb-3">
                                                <Form.Label
                                                    className="my-1 fw-bolder"
                                                    htmlFor="addFirstName"
                                                >
                                                    First Name
                                                </Form.Label>
                                                <Form.Control
                                                    id="addFirstName"
                                                    className="form-control w-100 col"
                                                    type="text"
                                                    onChange={element => setFirstName(element.target.value)}
                                                    value={firstName}
                                                    required
                                                    autoFocus
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bolder" htmlFor="addLastName">Last Name</Form.Label>
                                                <Form.Control
                                                    id="addLastName"
                                                    className="form-control w-100 col"
                                                    type="text"
                                                    onChange={element => setLastName(element.target.value)}
                                                    value={lastName}
                                                    required
                                                />
                                            </Form.Group>
                                            <p className="text-light">
                                                <small>
                                                    Once you change your name, it may take 5-10 minutes to propagate to other Foqal systems.
                                                    If you still see the old name after a few minutes, please contact support.
                                                </small>
                                            </p>
                                            <div className="d-flex justify-content-end mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-none text-danger me-3"
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setFirstName(data?.session?.user?.firstName ?? "");
                                                        setLastName(data?.session?.user?.lastName ?? "");
                                                    }}
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-sm"
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </form>
                            </div>
                        )}

                        <div className={theme.isV3 ? Styles["connections-container"] : ""}>
                            {user.emailAddresses?.isNotEmpty && (
                                <div className="mt-3">
                                    <div>
                                        <hr />
                                        <h6 className="pb-1">
                                            Email Addresses
                                        </h6>
                                    </div>

                                    {user.emailAddresses?.map(email => (
                                        <div key={email}>
                                            {theme.isV3
                                                ? <RiMailLine />
                                                : <AiOutlineMail className="text-light" />}
                                            {" "}
                                            {email}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {user.userGroups.isNotEmpty && (
                                <div className="mt-3">
                                    <div>
                                        <hr />
                                        <h6 className="pb-1">
                                            Groups
                                        </h6>
                                    </div>

                                    {user.userGroups?.map(group => (
                                        <div key={group.id}>
                                            <BiGroup className="text-light" />
                                            {" "}
                                            {group.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {applicationHandles.isNotEmpty && (
                                <div className="mt-3">
                                    <div>
                                        <hr />
                                        <h6 className="pb-1">
                                            Application Connections
                                        </h6>
                                    </div>

                                    {applicationHandles.map(([appName, handles]) => (
                                        <div
                                            className={theme.isV3 ? Styles["connection-item"] : "mb-3"}
                                            key={appName}
                                        >
                                            <div className={Styles["connection-title"]}>
                                                {appName}
                                            </div>
                                            {handles.map(handle => {
                                                const icon = APP_TO_ICON[
                                                    handle.application?.applicationName as keyof typeof APP_TO_ICON
                                                ] ?? <IoApps />;
                                                return (

                                                    <div
                                                        className="
                                                        d-flex w-100 justify-content-between list-group-item align-items-center"
                                                        key={handle.id}
                                                    >
                                                        <div className="d-flex flex-grow-1 align-items-center">
                                                            {handle.application && (
                                                                <div className="d-flex align-items-center">
                                                                    {icon}
                                                                    {" "}
                                                                    {handle.application.displayText}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    ))}

                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {theme.isV3 ? (
                    <SettingsContainer className="col" navigation="none">
                        <SettingsContainer.Section title="Profile Settings">
                            <SettingsContainer.Setting
                                title="Assignment Availability"
                                description={"This setting controls if tickets should be assigned to you or not. "
                                        + "By default, before assigning a ticket to you, Foqal Agent will try to "
                                        + "automatically determine if you are available using your status in systems "
                                        + "such as Slack. However, you can override this behavior and set yourself as "
                                        + "always available or always unavailable."}
                                permissions={SettingsContainer.Setting.Permissions.Everyone}
                            >
                                <Select
                                    className={Styles.select}
                                    placeholder=""
                                    onChange={value => setAssignmentPreference(value.value as AgentAssignmentPreference)}
                                    options={ASSIGNMENT_PREFERENCES}
                                    value={ASSIGNMENT_PREFERENCES.find(option => option.value === assignmentPreference)}
                                />
                            </SettingsContainer.Setting>

                        </SettingsContainer.Section>
                        <SettingsContainer.Section title="Notification Settings">
                            <SettingsContainer.Setting
                                className={Styles["sound-setting"]}
                                title="New Message Sound"
                                description={"The setting controls the sound notification you will hear when a new message"
                                        + " is received. You can choose to have no sound one of the following options."}
                                permissions={SettingsContainer.Setting.Permissions.Everyone}
                            >
                                <Select
                                    className={Styles.select}
                                    placeholder=""
                                    onChange={value => saveSounds({
                                        ...sounds,
                                        message: value.value as AgentSoundPreference,
                                    })}
                                    options={NOTIFICATION_PREFERENCES}
                                    value={NOTIFICATION_PREFERENCES.find(
                                        option => option.value === (sounds.message ?? AgentSoundPreference.Bubble),
                                    )}
                                />
                                {sounds.message !== AgentSoundPreference.None && (
                                    <Button
                                        size="small"
                                        variant="outline-gray"
                                        prefixIcon={<RiPlayCircleFill />}
                                        onClick={() => notifications.play(sounds.message ?? AgentSoundPreference.Bubble)}
                                    />
                                )}

                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                className={Styles["sound-setting"]}
                                title="New Chat Sound"
                                description={"The setting controls the sound notification you will hear when a new conversation"
                                        + " is started. You can choose to have no sound one of the following options."}
                                permissions={SettingsContainer.Setting.Permissions.Everyone}
                            >
                                <Select
                                    className={Styles.select}
                                    placeholder=""
                                    onChange={value => saveSounds({
                                        ...sounds,
                                        chat: value.value as AgentSoundPreference,
                                    })}
                                    options={NOTIFICATION_PREFERENCES}
                                    value={NOTIFICATION_PREFERENCES.find(
                                        option => option.value === (sounds.chat ?? AgentSoundPreference.Beyond),
                                    )}
                                />
                                {sounds.chat !== AgentSoundPreference.None && (
                                    <Button
                                        size="small"
                                        variant="outline-gray"
                                        prefixIcon={<RiPlayCircleFill />}
                                        onClick={() => notifications.play(sounds.chat ?? AgentSoundPreference.Beyond)}
                                    />
                                )}
                            </SettingsContainer.Setting>
                        </SettingsContainer.Section>
                    </SettingsContainer>
                ) : (
                    <div className="col shadow p-4 bg-white">
                        <h5 className="">
                            Preferences
                        </h5>

                        <div className="row mt-3 ">
                            <Form.Group className="col-xl-6">

                                <Form.Label className="fw-bolder mt-0">Assignment Availability</Form.Label>

                                <Select
                                    className="text-gray"
                                    placeholder=""
                                    onChange={value => setAssignmentPreference(value.value as AgentAssignmentPreference)}
                                    options={ASSIGNMENT_PREFERENCES}
                                    value={ASSIGNMENT_PREFERENCES.find(option => option.value === assignmentPreference)}
                                />
                                <Form.Text className="text-light">
                                    This setting controlls if tickets should be assigned to you or not. By default, before
                                    assigning a ticket to you, Foqal Agent will try to automatically determine if you are
                                    available using your status in systems such as Slack.
                                    However, you can override this behavior and set yourself as always
                                    available or always unavailable.
                                </Form.Text>

                            </Form.Group>

                        </div>

                    </div>
                )}

            </div>
        </>
    );
}
